/**
 * Local Storage key helper
 *
 * Centralizes the logic for prefixing local storage keys so that different funnels
 * don't collide when accessed in the same browser.
 *
 * Behavior:
 * - Uses the `storageKeyName` from the funnel's config.json to prefix keys
 * - For variant-cv-1: keys are prefixed with `sl_variant_cv_`
 * - For variant-cv-nad: keys are prefixed with `sl_variant_cv_nad_`
 * - Includes backward compatibility for variant-cv-1's legacy unprefixed keys
 *
 * Usage:
 *   import { getLocalStorageKey, localStore } from './localStorageKey'
 *   localStore.getItem('abandoned_cart_timer_id') // → reads "sl_variant_cv_abandoned_cart_timer_id" on /cv-1
 *   localStore.setItem('abandoned_cart_start_time', Date.now())
 *   localStore.removeItem('abandoned_cart_checkout_logged')
 *
 * Or use getLocalStorageKey directly:
 *   localStorage.getItem(getLocalStorageKey('abandoned_cart_timer_id'))
 */

// Cache for funnel config to avoid repeated lookups
let cachedConfig = null
let cachedStorageKeyName = null

/**
 * Determine the funnel route from the current pathname.
 * @returns {string|null} The funnel route (e.g., '/cv-1', '/nad') or null
 */
const getFunnelRoute = () => {
  if (typeof window === 'undefined' || !window.location) {
    return null
  }
  const pathname = window.location.pathname || ''
  
  // Check for NAD funnel routes
  if (pathname.startsWith('/nad')) {
    return '/nad'
  }
  
  // Check for Sermorelin funnel routes
  if (pathname.startsWith('/sermorelin')) {
    return '/sermorelin'
  }
  
  // Check for CV-3 funnel routes
  if (pathname.startsWith('/cv-3')) {
    return '/cv-3'
  }

  // Check for CV-4 funnel routes
  if (pathname.startsWith('/cv-4')) {
    return '/cv-4'
  }

  // Check for CV-2 funnel routes
  if (pathname.startsWith('/cv-2')) {
    return '/cv-2'
  }

  // Check for CV-1 funnel routes
  if (pathname.startsWith('/cv-1')) {
    return '/cv-1'
  }
  
  // Check for payment return routes
  if (pathname.startsWith('/payment/return/nad')) {
    return '/nad'
  }
  if (pathname.startsWith('/payment/return/sermorelin')) {
    return '/sermorelin'
  }
  if (pathname.startsWith('/payment/return/cv4')) {
    return '/cv-4'
  }
  if (pathname.startsWith('/payment/return/cv3')) {
    return '/cv-3'
  }
  if (pathname.startsWith('/payment/return/cv2')) {
    return '/cv-2'
  }
  if (pathname.startsWith('/payment/return/cv')) {
    return '/cv-1'
  }
  
  return null
}

/**
 * Get the storageKeyName from the funnel config.
 * Attempts to read from the FunnelContext or fallback to route-based detection.
 * @param {Object} config - Optional funnel config object from FunnelContext
 * @returns {string|null} The storage key name (e.g., 'sl_variant_cv', 'sl_variant_cv_nad')
 */
const getStorageKeyName = (config = null) => {
  // If config is provided and has storageKeyName, use it
  if (config?.storageKeyName) {
    cachedConfig = config
    cachedStorageKeyName = config.storageKeyName
    return config.storageKeyName
  }
  
  // If we have a cached value, use it
  if (cachedStorageKeyName) {
    return cachedStorageKeyName
  }
  
  // Fallback: Determine from route
  const route = getFunnelRoute()
  if (route === '/nad') {
    return 'sl_variant_cv_nad'
  }
  if (route === '/sermorelin') {
    return 'sl_variant_cv_sermorelin'
  }
  if (route === '/cv-2') {
    return 'sl_variant_cv'
  }
  if (route === '/cv-3') {
    return 'sl_variant_cv_3'
  }
  if (route === '/cv-4') {
    return 'sl_variant_cv_4'
  }
  if (route === '/cv-1') {
    return 'sl_variant_cv'
  }
  
  return null
}

/**
 * Prefix the provided local storage key with the funnel-specific prefix.
 * Returns the key unchanged if no prefix is determined.
 *
 * Idempotent: a key that's already prefixed is returned as-is.
 *
 * @param {string} key - Raw local storage key
 * @param {Object} config - Optional funnel config object
 * @returns {string} The (possibly) prefixed key
 */
export const getLocalStorageKey = (key, config = null) => {
  if (!key || typeof key !== 'string') {
    return key
  }
  
  const storageKeyName = getStorageKeyName(config)
  
  // If no storage key name, return as-is
  if (!storageKeyName) {
    return key
  }
  
  const prefix = `${storageKeyName}_`
  
  // If already prefixed, return as-is (idempotent)
  if (key.startsWith(prefix)) {
    return key
  }
  
  return `${prefix}${key}`
}

/**
 * Get item from localStorage with backward compatibility.
 * First tries prefixed key, then falls back to unprefixed key (for variant-cv-1 legacy support).
 *
 * @param {string} key - Raw key name
 * @param {Object} config - Optional funnel config object
 * @returns {string|null} The stored value or null
 */
const getItemWithFallback = (key, config = null) => {
  if (typeof window === 'undefined' || !window.localStorage) return null
  
  const prefixedKey = getLocalStorageKey(key, config)
  
  // Try prefixed key first
  let value = window.localStorage.getItem(prefixedKey)
  
  // Backward compatibility: If no value found and we're on variant-cv-1,
  // try the unprefixed key (legacy behavior)
  if (value === null && getFunnelRoute() === '/cv-1') {
    value = window.localStorage.getItem(key)
    
    // If found legacy value, migrate it to the new prefixed key
    if (value !== null) {
      window.localStorage.setItem(prefixedKey, value)
      window.localStorage.removeItem(key) // Clean up old key
    }
  }
  
  return value
}

/**
 * Set item in localStorage with the appropriate prefix.
 *
 * @param {string} key - Raw key name
 * @param {string} value - Value to store
 * @param {Object} config - Optional funnel config object
 */
const setItemWithPrefix = (key, value, config = null) => {
  if (typeof window === 'undefined' || !window.localStorage) return
  
  const prefixedKey = getLocalStorageKey(key, config)
  window.localStorage.setItem(prefixedKey, value)
}

/**
 * Remove item from localStorage, handling both prefixed and unprefixed keys.
 *
 * @param {string} key - Raw key name
 * @param {Object} config - Optional funnel config object
 */
const removeItemWithPrefix = (key, config = null) => {
  if (typeof window === 'undefined' || !window.localStorage) return
  
  const prefixedKey = getLocalStorageKey(key, config)
  
  // Remove prefixed key
  window.localStorage.removeItem(prefixedKey)
  
  // Backward compatibility: Also remove unprefixed key if on variant-cv-1
  if (getFunnelRoute() === '/cv-1') {
    window.localStorage.removeItem(key)
  }
}

/**
 * Convenience wrappers around `window.localStorage` that automatically
 * apply funnel-specific prefixing with backward compatibility.
 * SSR-safe: returns null/no-op when the `localStorage` API isn't available.
 */
export const localStore = {
  /**
   * Get item with automatic fallback to unprefixed key for backward compatibility
   */
  getItem(key, config = null) {
    return getItemWithFallback(key, config)
  },
  
  /**
   * Set item with funnel-specific prefix
   */
  setItem(key, value, config = null) {
    setItemWithPrefix(key, value, config)
  },
  
  /**
   * Remove item (both prefixed and unprefixed versions)
   */
  removeItem(key, config = null) {
    removeItemWithPrefix(key, config)
  },
}

export default localStore
